import React, { useState } from "react";
import startCase from "lodash/startCase";
import { formatINR,sumField,safeArray } from "egov-ui-kit/utils/commons";

const statusClassMap = {
  Active: "status-active",
  "In Progress": "status-in-progress",
  Inactive: "status-inactive",
  Completed: "status-completed",
};

const statusColorMap = {
  Active: { color: "#1a7f37", background: "#dafbe1", border: "1px solid #b4f0c4" },
  "In Progress": { color: "#9a6700", background: "#fff8c5", border: "1px solid #f0dd8f" },
  Inactive: { color: "#6e7781", background: "#eef0f2", border: "1px solid #d8dee4" },
  Completed: { color: "#0969da", background: "#ddf4ff", border: "1px solid #b6e3ff" },
};

const StatusBadge = ({ status }) => (
  <span
    className={`status-badge ${statusClassMap[status] || ""}`}
    style={{
      ...(statusColorMap[status] || {}),
      padding: "2px 10px",
      borderRadius: "999px",
      fontSize: "0.85em",
      fontWeight: 600,
      display: "inline-block",
      whiteSpace: "nowrap",
    }}
  >
    {status}
  </span>
);

// ─────────────────────────────────────────────
// HELPER: Get purchase orders for a supplier receipt.
// Reads the receipt's own `purchaseOrders` array (this is where the
// earlier bug was — it was reading `receipt.accountHead`, a field that
// doesn't exist, so the table always came back empty).
// ─────────────────────────────────────────────
const buildPurchaseOrders = (receipt) => safeArray(receipt.purchaseOrders);

// ─────────────────────────────────────────────
// PURCHASE ORDER DETAIL TABLE (3rd level drill-down)
// ─────────────────────────────────────────────
const PurchaseOrderDetailTable = ({ receipt, financialYear, ulbName, onBack }) => {
  const purchaseOrders = buildPurchaseOrders(receipt);
  const totalValue = sumField(purchaseOrders, "purchaseOrderAmount");

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{startCase(receipt.supplierName)}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      {/* DETAIL TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Account Code</th>
              <th>Purchase Order Date</th>
              <th>Purchase Order Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {purchaseOrders.length > 0 ? (
              <React.Fragment>
                {purchaseOrders.map((p) => (
                  <tr key={p.id}>
                    <td>{p.accountName}</td>
                    <td>{p.accountCode}</td>
                    <td>{p.purchaseOrderDate}</td>
                    <td>{formatINR(p.purchaseOrderAmount)}</td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
                <tr className="total-row" style={{ fontWeight: "bold" }}>
                  <td colSpan="3">Total</td>
                  <td>{formatINR(totalValue)}</td>
                  <td></td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No Purchase Order Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// RECEIPT DETAIL COMPONENT (2nd level drill-down, directly under ULB)
// ─────────────────────────────────────────────
const STATUS_OPTIONS = ["All", "Active", "In Active"];

const ReceiptDetailTable = ({ item, financialYear, ulbName, onBack }) => {
  const receipts = safeArray(item.supplierReceipts);
  const [selectedPurchaseOrderReceipt, setSelectedPurchaseOrderReceipt] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredReceipts = statusFilter === "All" ? receipts : receipts.filter((r) => r.status === statusFilter);

  // Dynamic aggregates for the total row
  const totalOrders = filteredReceipts.reduce((acc, r) => acc + Number(r.totalPurchaseOrders || 0), 0);
  const totalAmount = filteredReceipts.reduce((acc, r) => acc + Number(r.amount || 0), 0);

const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    const aAmount = Number(a.amount || 0);
    const bAmount = Number(b.amount || 0);
    return bAmount - aAmount; // higher amount first; 0s sink to the bottom
  });

  // 3rd level: individual purchase orders for a clicked supplier receipt
  if (selectedPurchaseOrderReceipt) {
    return (
      <PurchaseOrderDetailTable
        receipt={selectedPurchaseOrderReceipt}
        financialYear={financialYear}
        ulbName={ulbName}
        onBack={() => setSelectedPurchaseOrderReceipt(null)}
      />
    );
  }

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{startCase(item.name)}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      {/* STATUS FILTER */}
      {
        item.supplierReceipts && <div
        className="status-filter-bar"
        style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 0" }}
      >
        <label htmlFor="supplierStatusFilter" style={{ fontWeight: 600 }}>
          Status :
        </label>
        <select
          id="supplierStatusFilter"
          className="form-control"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: "auto", display: "inline-block" }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
      }

      {/* DETAIL TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Total Number of Purchase Orders</th>
              <th>Total Purchase Orders Amount</th>
            </tr>
          </thead>
          <tbody>
            {sortedReceipts.length > 0 ? (
              <React.Fragment>
                {sortedReceipts.map((r) => (
                  <tr key={r.id}>
                    <td>{r.supplierName}</td>
                    <td>{r.totalPurchaseOrders}</td>

                    <td>
                      {r.amount > 0 ? (
                        <a
                          href="#"
                          className="link-cell"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedPurchaseOrderReceipt(r);
                          }}
                        >
                          {formatINR(r.amount)}
                        </a>
                      ) : (
                        formatINR(r.amount)
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="total-row" style={{ fontWeight: "bold" }}>
                  <td colSpan="1">Total</td>
                  <td>{totalOrders}</td>
                  <td>{formatINR(totalAmount)}</td> 
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No Supplier Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT (ULB Wise No of Suppliers → Supplier Receipts → Purchase Orders)
// ─────────────────────────────────────────────
const SupplierTable = ({ supplierDetails }) => {
  // const tableData = supplierDetails ? supplierDetails : zoneData;
  const tableData = supplierDetails && supplierDetails;
  const totalSuppliers = safeArray(tableData.ulbs).reduce((acc, ulb) => acc + (Number(ulb.totalSupplier) || 0), 0);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  if (selectedReceipt) {
    return (
      <ReceiptDetailTable
        item={selectedReceipt}
        financialYear={tableData.financialYear}
        ulbName={tableData.ulbName}
        onBack={() => setSelectedReceipt(null)}
      />
    );
  }

  const currentRows = safeArray(tableData.ulbs);
  const totalsRow = tableData.totals;

  const handleDrillDown = (item) => {
    setSelectedReceipt(item);
  };

  return (
    <div className="receipt-card">
      {/* HEADER */}
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <div className="icon-wrapper">
            <i className="material-icons">description</i>
          </div>
          <div>
            <h2>ULB Wise No of Suppliers</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {/* MAIN DRILL DOWN TABLE */}
      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>ULB Name</th>
              <th>Total Suppliers</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? (
              <React.Fragment>
                {currentRows.map(function (item) {
                  return (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>
                        {item.totalSupplier > 0 ? (
                          <a
                            href="#"
                            className="link-cell"
                            onClick={function (e) {
                              e.preventDefault();
                              handleDrillDown(item);
                            }}
                          >
                            {formatINR(item.totalSupplier)}
                          </a>
                        ) : (
                          formatINR(item.totalSupplier)
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td>Total</td>
                  <td>{formatINR(totalSuppliers)}</td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="2" style={{ textAlign: "center" }}>
                  No Record Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierTable;


// WEBPACK FOOTER //
// src/modules/employee/Home/SupplierTabls.js